import { useCallback, useMemo } from "react";
import { Dp } from "./CoState";
import { Filter } from "./CoQuery";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { make as AugmentName } from "common/src/tft-static/AugmentName.bs";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { searchAugments } from "../common/Search2";
import { augments } from "common/src/data/Data.bs";
import { make as AugmentTooltip } from "common/src/tft-static/AugmentTooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";

const cols = [
  makeCol({
    width: 170,
    order: ([augmentId], _, { augmentsT }) => augmentsT(augmentId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Augment")} </>,
    renderValue: ([augId], { onExpand }, { augmentsT }) => (
      <div
        className="flex items-center text-sm cursor-pointer"
        onClick={(_) =>
          onExpand({
            typ: "a",
            id: augId,
            sid: String(augId),
            pick: 0,
            label: augmentsT(augId),
          })
        }
      >
        <div className="flex-shrink-0">
          <AugmentTooltip id={augId}>
            <AugmentImage augId={augId} size={28} />
          </AugmentTooltip>
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          <AugmentName id={augId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 80,
    order: ([_, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 70,
    order: ([_, v]) => v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, v]) => <> {(v.place / v.count).toFixed(2)} </>,
  }),
  makeCol({
    width: 82,
    order: ([_, v]) => v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.top4 / v.count)} </>,
  }),
  makeCol({
    width: 72,
    order: ([_, v]) => v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.won / v.count)} </>,
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, v], { minSample }) =>
      v && v.count >= minSample ? v.place / v.count : undefined,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("At 2-1")}</>,
    renderValue: ([, , v], { minSample }) => {
      if (v && v.place / v.count < 7.5) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 1st augment (stage 2-1)`}
          >
            <div className={minSample > v.count ? "text-white3" : ""}>
              {(v.place / v.count).toFixed(2)}
            </div>
          </Tooltip>
        );
        // return <> {humanReadableCount(v.count)} </>;
      } else {
        return <>—</>;
      }
    },
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, _3, v], { minSample }) =>
      v && v.count >= minSample ? v.place / v.count : undefined,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("3-2")}</>,
    renderValue: ([_1, _2, _3, v], { minSample }) => {
      if (v) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 2nd augment (stage 3-2)`}
          >
            <div className={minSample > v.count ? "text-white3" : ""}>
              {(v.place / v.count).toFixed(2)}
            </div>
          </Tooltip>
        );
      } else {
        return <> — </>;
      }
    },
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, _3, _4, v], { minSample }) =>
      v && v.count >= minSample ? v.place / v.count : undefined,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("4-2")}</>,
    renderValue: ([_1, _2, _3, _4, v], { minSample }) => {
      if (v) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 3rd augment (stage 4-2)`}
          >
            <div className={minSample > v.count ? "text-white3" : ""}>
              {(v.place / v.count).toFixed(2)}{" "}
            </div>
          </Tooltip>
        );
      } else {
        return <>—</>;
      }
    },
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: ([augId], { onExpand, hideExpand }, { augmentsT }) => {
      return hideExpand ? (
        <div />
      ) : (
        <IconButton
          onClick={(_) =>
            onExpand({
              typ: "a",
              id: augId,
              sid: String(augId),
              pick: 0,
              label: augmentsT(augId),
            })
          }
          classes={{
            root: "text-lg",
          }}
        >
          <OpenInFull fontSize="inherit" />
        </IconButton>
      );
    },
  }),
];

export type AugmentFilters = {
  silvers: boolean;
  golds: boolean;
  prisms: boolean;
};

type Props = {
  data: Array<[string, Dp, Dp | null, Dp | null, Dp | null]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  search: string;
  augmentFilters: AugmentFilters;
  minSample: number;
};

const CoAugments = ({
  data,
  baseCount,
  onExpand,
  hideExpand,
  search,
  augmentFilters,
  minSample,
}: Props) => {
  const set = useSet();
  const keyFn = useCallback(([id, _v]: [number, Dp]) => String(id), []);
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand, minSample }),
    [baseCount, onExpand, hideExpand, minSample],
  );

  const visibleFilter = useMemo(() => {
    const showAllTiers =
      (augmentFilters.silvers &&
        augmentFilters.golds &&
        augmentFilters.prisms) ||
      (!augmentFilters.silvers &&
        !augmentFilters.golds &&
        !augmentFilters.prisms);

    let passSearch: any;
    if (search === "") {
      passSearch = () => true;
    } else {
      const passIds = searchAugments(search, set);
      passSearch = (augId: string) => passIds.has(augId);
    }

    let passTier: any;
    if (showAllTiers) {
      passTier = () => true;
    } else {
      passTier = (augId: string) => {
        const info = augments(set)[augId];
        if (info.rank === 1) {
          return augmentFilters.silvers;
        } else if (info.rank === 2) {
          return augmentFilters.golds;
        } else if (info.rank === 3) {
          return augmentFilters.prisms;
        } else {
          return false;
        }
      };
    }

    return ([augId]: [string]) => passTier(augId) && passSearch(augId);
  }, [
    search,
    augmentFilters.silvers,
    augmentFilters.golds,
    augmentFilters.prisms,
  ]);

  const tableClass = [];

  const filteredData = useMemo(
    () => data.filter(([_, v]) => v.count >= minSample),
    [data, minSample],
  );

  let hasResults =
    search === "" || filteredData.some((v) => visibleFilter([String(v[0])]));

  if (filteredData.length === 0) {
    return (
      <div className="px-5 py-6 text-xl text-medium text-white2">
        No augment combos with at least {minSample} games played
      </div>
    );
  } else {
    return (
      <div>
        <StatsTable
          cols={cols}
          context={context}
          data={filteredData}
          keyFn={keyFn}
          stickyColCount={1}
          defaultSortIdx={1}
          top="t262"
          visibleFilter={visibleFilter}
        />
        {hasResults ? null : (
          <div className="p-5 text-xl text-medium text-white2">
            No results found matching the search
          </div>
        )}
      </div>
    );
  }
};

export default CoAugments;
